import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";
import { CSRF_HEADER, csrfToken } from "@/lib/csrf";

/** Client base path. Resolved against the page origin so it also works under jsdom. */
export const API_BASE = "/api";

function baseUrl(): string {
  return typeof window === "undefined" ? API_BASE : `${window.location.origin}${API_BASE}`;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/** Paths where a 401 is a real answer, never a reason to refresh. */
const NO_REFRESH = new Set([
  "/auth/login",
  "/auth/refresh",
  "/auth/accept-invite",
  "/auth/reset-password",
  "/auth/forgot-password",
]);

type AuthLostHandler = (nextPath: string) => void;

const PUBLIC_PREFIXES = ["/login", "/set-password", "/reset-password", "/forgot-password", "/signup", "/privacy", "/terms"];

function defaultAuthLost(nextPath: string): void {
  const here = window.location.pathname;
  if (PUBLIC_PREFIXES.some((p) => here.startsWith(p))) return;
  window.location.assign(`/login?next=${encodeURIComponent(nextPath)}`);
}

let authLostHandler: AuthLostHandler = defaultAuthLost;

/** Replace what happens when the session cannot be refreshed. `null` restores the default. */
export function setAuthLostHandler(fn: AuthLostHandler | null): void {
  authLostHandler = fn ?? defaultAuthLost;
}

function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function onAuthLost(): void {
  authLostHandler(currentPath());
}

function withCsrf(request: Request): Request {
  if (SAFE_METHODS.has(request.method.toUpperCase())) return request;
  const token = csrfToken();
  if (token) request.headers.set(CSRF_HEADER, token);
  return request;
}

let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  try {
    const headers = new Headers();
    const token = csrfToken();
    if (token) headers.set(CSRF_HEADER, token);
    const res = await globalThis.fetch(`${baseUrl()}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers,
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Single-flight refresh: concurrent callers share one `POST /auth/refresh`.
 * When it fails, `onAuthLost` runs once for the whole batch.
 */
export function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= doRefresh()
    .then((ok) => {
      if (!ok) onAuthLost();
      return ok;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

const pendingClones = new Map<string, Request>();

const authMiddleware: Middleware = {
  onRequest({ request, schemaPath, id }) {
    const req = withCsrf(request);
    if (!NO_REFRESH.has(schemaPath)) pendingClones.set(id, req.clone());
    return req;
  },
  async onResponse({ response, schemaPath, id }) {
    const clone = pendingClones.get(id);
    pendingClones.delete(id);
    if (response.status !== 401 || NO_REFRESH.has(schemaPath) || !clone) return response;
    const ok = await refreshOnce();
    if (!ok) return response;
    const retry = await globalThis.fetch(withCsrf(clone));
    if (retry.status === 401) onAuthLost();
    return retry;
  },
  onError({ id }) {
    pendingClones.delete(id);
  },
};

export const api = createClient<paths>({
  baseUrl: baseUrl(),
  credentials: "include",
  // Late-bound so test interceptors (msw) that patch globalThis.fetch are honoured.
  fetch: (req: Request) => globalThis.fetch(req),
});
api.use(authMiddleware);
